import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTION_TYPES, useAmendmentSearchDispatch, useAmendmentSearchState } from './amendment-search-context';
import AmendmentAppService from '../../../../application/platform/sge/amendment/amendment-app-service';
import ParlamentarianAppService from '../../../../application/platform/sge/parlamentarian/parlamentarian-app-service';
import PoliticalPartyAppService from '../../../../application/platform/sge/political-party/political-party-app-service';
import BudgetUnitAppService from '../../../../application/platform/sge/budget-unit/budget-unit-app-service';
import BudgetProgramAppService from '../../../../application/platform/sge/budget-program/budget-program-app-service';
import BudgetActionAppService from '../../../../application/platform/sge/budget-action/budget-action-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { IAmendmentPagedResponse } from '../../../../model/sge/amendment/response/amendment-paged-response';
import { parseNumberInput } from '../../../../utils/format';
import { EMPTY_AMENDMENT_FILTERS } from '../components/amendment-filter-utils';

// Campos de filtro numéricos: nome do input → parâmetro da consulta
const NUMERIC_FIELDS: Record<string, string> = {
    'input-year': 'year',
    'select-status': 'status',
    'select-type': 'type',
    'select-parlamentar': 'parlamentarId',
    'select-political-party': 'politicalPartyId',
    'select-budget-action': 'budgetActionId',
    'input-min-value': 'minValue',
    'input-max-value': 'maxValue',
};

const TEXT_FIELDS: Record<string, string> = {
    'input-search': 'search',
    'input-start-date': 'startDate',
    'input-end-date': 'endDate',
};

export const useAmendmentSearchController = () => {
    const navigate = useNavigate();
    const dispatch = useAmendmentSearchDispatch();
    const state = useAmendmentSearchState();
    const { showSuccess, showError, confirmDelete } = useFeedback();
    const { budgetUnitId, budgetProgramId } = state.parameters;

    useEffect(() => {
        ParlamentarianAppService.list({ page: 0, pageSize: 100 })
            .then((result) => dispatch({ type: ACTION_TYPES.setParlamentarians, payload: result.items }))
            .catch((error: any) => showError('Não foi possível carregar os parlamentares', error));
        PoliticalPartyAppService.listAll()
            .then((parties) => dispatch({ type: ACTION_TYPES.setPoliticalParties, payload: parties }))
            .catch((error: any) => showError('Não foi possível carregar os partidos políticos', error));
        BudgetUnitAppService.list()
            .then((budgetUnits) => dispatch({ type: ACTION_TYPES.setBudgetUnits, payload: budgetUnits }))
            .catch((error: any) => showError('Não foi possível carregar as unidades orçamentárias', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cascata unidade → programa → ação
    useEffect(() => {
        if (!budgetUnitId) {
            dispatch({ type: ACTION_TYPES.setBudgetPrograms, payload: [] });
            return;
        }
        BudgetProgramAppService.list({ budgetUnitId })
            .then((programs) => dispatch({ type: ACTION_TYPES.setBudgetPrograms, payload: programs }))
            .catch((error: any) => showError('Não foi possível carregar os programas orçamentários', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budgetUnitId]);

    useEffect(() => {
        if (!budgetProgramId) {
            dispatch({ type: ACTION_TYPES.setBudgetActions, payload: [] });
            return;
        }
        BudgetActionAppService.list({ budgetProgramId })
            .then((actions) => dispatch({ type: ACTION_TYPES.setBudgetActions, payload: actions }))
            .catch((error: any) => showError('Não foi possível carregar as ações orçamentárias', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budgetProgramId]);

    // Filtros
    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'select-budget-unit') {
            dispatch({ type: ACTION_TYPES.updateParameter, payload: { budgetUnitId: parseNumberInput(value), budgetProgramId: undefined, budgetActionId: undefined } });
        } else if (name === 'select-budget-program') {
            dispatch({ type: ACTION_TYPES.updateParameter, payload: { budgetProgramId: parseNumberInput(value), budgetActionId: undefined } });
        } else if (name in NUMERIC_FIELDS) {
            dispatch({ type: ACTION_TYPES.updateParameter, payload: { [NUMERIC_FIELDS[name]]: parseNumberInput(value) } });
        } else if (name in TEXT_FIELDS) {
            dispatch({ type: ACTION_TYPES.updateParameter, payload: { [TEXT_FIELDS[name]]: value || undefined } });
        }
    }

    function handleSearch() {
        dispatch({ type: ACTION_TYPES.updateParameter, payload: { page: 0 } });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function handleClearFilters() {
        dispatch({ type: ACTION_TYPES.updateParameter, payload: { ...EMPTY_AMENDMENT_FILTERS, page: 0 } });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function handlePaginationChange(page: number, pageSize: number) {
        dispatch({ type: ACTION_TYPES.updateParameter, payload: { page, pageSize } });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    // Navegação
    function navigateToCreate() { navigate('/emendas/cadastrar'); }
    function navigateToDetail(id: number) { navigate(`/emendas/${id}`); }
    function navigateToEdit(id: number) { navigate(`/emendas/editar/${id}`); }

    async function removeAmendment(row: IAmendmentPagedResponse) {
        try {
            await AmendmentAppService.remove(row.id);
            showSuccess('Emenda excluída com sucesso!');
            dispatch({ type: ACTION_TYPES.triggerSearch });
        } catch (error: any) {
            showError('Não foi possível excluir a emenda', error);
        }
    }

    function handleDelete(row: IAmendmentPagedResponse) {
        confirmDelete(`Tem certeza que deseja excluir a emenda "${row.name || ''}"?`, () => { removeAmendment(row); });
    }

    return { handleOnChange, handleSearch, handleClearFilters, handlePaginationChange, navigateToCreate, navigateToDetail, navigateToEdit, handleDelete };
};
